import { BadgeDelta, Card, Metric, Text } from '@tremor/react';
import { formatAmount } from '../../../../util';
import { FC } from 'react';
import { ReactComponent as InfoIcon } from '../../../../assets/svg/InfoIconLight.svg';
import { UNIT } from '../../../../ts-components/page-dashboard-V2/helper';
import TooltipContainer from '../../TooltipContainer/TooltipContainer';
import { useTranslation } from 'react-i18next';
import styles from './SummaryCard.module.scss';

type Props = {
  title?: string,
  value?: string | number,
  value2?: string | number,
  infoText?: string,
  unit?: UNIT,
  badgePercentage?: number,
  size?: 'small' | 'default',
}

const SummaryCard: FC<Props> = ({ title, value, value2, infoText, unit, badgePercentage, size = 'default' }) => {
  const [t] = useTranslation('shared', { keyPrefix: 'summaryCard' });

  const formatValue = (value: string | number | undefined, unit?: UNIT) => {
    let formattedValue = formatAmount(value ?? 0) || '0';
    if (unit === UNIT.PERCENTAGE) {
      formattedValue = t('percentageValue', '{{value}}%', { value: formattedValue });
    } else if (unit === UNIT.AMOUNT) {
      formattedValue = t('currencyValue', '{{value}} kr', { value: formattedValue });
    }
    return formattedValue;
  }

  const metricText = `${formatValue(value, unit)}${value2 !== undefined ? ` / ${formatValue(value2, unit)}` :''}`;

  const isSmall = size === 'small';

  return (
    <div>
      <Card className={`${styles['summary-card']} ${isSmall ? styles['summary-card--small'] : ''}`}>
        <div className={`${styles['summary-card__title-container']} ${isSmall ? styles['summary-card__title-container--small'] : ''}`}>
          <Text className={styles['summary-card__title']}>
            {title}
          </Text>
          {infoText && (
            <TooltipContainer renderReferenceComponent={(className, ref) => (
              <div
                className={`${className} ${styles['summary-card__info-icon']} ${isSmall ? styles['summary-card__info-icon--small'] : ''}`}
                ref={ref}
              >
                <InfoIcon />
              </div>
            )}>
              <div className={`${styles['summary-card__tooltip']} ${isSmall ? styles['summary-card__tooltip--small'] : ''}`}>
                <p className={`${styles['summary-card__tooltip-title']} ${isSmall ? styles['summary-card__tooltip-title--small'] : ''}`}>{title}</p>
                <p className={`${styles['summary-card__tooltip-text']} ${isSmall ? styles['summary-card__tooltip-text--small'] : ''}`}>{infoText}</p>
              </div>
            </TooltipContainer>
          )}
          {badgePercentage && badgePercentage > 0 && (
            <BadgeDelta
              deltaType={badgePercentage >= 0 ? 'moderateIncrease' : 'moderateDecrease'}
              isIncreasePositive={true}
              size="xs"
            >{`${badgePercentage >= 0 ? '+' : '' }${formatAmount(badgePercentage ?? 0) || '0'} %`}</BadgeDelta>
          )}
        </div>
        <Metric className={`${styles['summary-card__metric']} ${isSmall ? styles['summary-card__metric--small'] : ''}`}>{metricText}</Metric>
      </Card>
    </div>
  )
}

export default SummaryCard;
